export const LightTokens = {
    deepest: "#eef1f6",
    bg: "#f6f7fa",
    card: "#ffffff",
    raised: "#f0f2f7",
    surface: "#f0f2f7",
    hairline: "rgba(15, 23, 42, 0.08)",
    hairlineStrong: "rgba(15, 23, 42, 0.16)",
    topHighlight: "inset 0 1px 0 rgba(255, 255, 255, 0.85)",

    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#64748b",

    accent: "#d97706",
    accentSubtle: "rgba(217, 119, 6, 0.10)",
    accentBorder: "rgba(217, 119, 6, 0.28)",
    primaryFill: "#0f172a",
    primaryText: "#ffffff",

    positive: "#059669",
    positiveBg: "rgba(5, 150, 105, 0.10)",
    positiveBorder: "rgba(5, 150, 105, 0.24)",
    negative: "#dc2626",
    negativeBg: "rgba(220, 38, 38, 0.10)",
    negativeBorder: "rgba(220, 38, 38, 0.24)",
    warning: "#d97706",
    warningBg: "#fffbeb",
    warningBorder: "rgba(217, 119, 6, 0.30)",
};

export const DarkTokens = {
    deepest: "#0f0f10",
    bg: "#141415",
    card: "#19191b",
    raised: "#232325",
    surface: "#232325",
    hairline: "rgba(237, 237, 237, 0.08)",
    hairlineStrong: "rgba(237, 237, 237, 0.14)",
    topHighlight: "inset 0 1px 0 rgba(255, 255, 255, 0.12)",

    textPrimary: "#ededed",
    textSecondary: "#a1a1a6",
    textMuted: "#85858b",

    accent: "#f0b90b",
    accentSubtle: "rgba(240, 185, 11, 0.12)",
    accentBorder: "rgba(240, 185, 11, 0.28)",
    primaryFill: "#ededed",
    primaryText: "#141415",

    positive: "#4cc38a",
    positiveBg: "rgba(76, 195, 138, 0.12)",
    positiveBorder: "rgba(76, 195, 138, 0.24)",
    negative: "#e5484d",
    negativeBg: "rgba(229, 72, 77, 0.12)",
    negativeBorder: "rgba(229, 72, 77, 0.24)",
    warning: "#e0c467",
    warningBg: "#262114",
    warningBorder: "#453a1d",
};

export const Tokens = {
    // Theme-reactive surface ramp (defaults to Light; switches via CSS custom properties)
    deepest: "var(--pf-deepest, #eef1f6)",
    bg: "var(--pf-bg, #f6f7fa)",
    card: "var(--pf-card, #ffffff)",
    raised: "var(--pf-raised, #f0f2f7)",
    surface: "var(--pf-surface, #f0f2f7)",
    hairline: "var(--pf-hairline, rgba(15, 23, 42, 0.08))",
    hairlineStrong: "var(--pf-hairline-strong, rgba(15, 23, 42, 0.16))",
    topHighlight:
        "var(--pf-top-highlight, inset 0 1px 0 rgba(255, 255, 255, 0.85))",

    // Three text roles
    textPrimary: "var(--pf-text-primary, #0f172a)",
    textSecondary: "var(--pf-text-secondary, #475569)",
    textMuted: "var(--pf-text-muted, #64748b)",

    // Single accent (ledger amber) & primary action
    accent: "var(--pf-accent, #d97706)",
    accentSubtle: "var(--pf-accent-subtle, rgba(217, 119, 6, 0.10))",
    accentBorder: "var(--pf-accent-border, rgba(217, 119, 6, 0.28))",
    primaryFill: "var(--pf-primary-fill, #0f172a)",
    primaryText: "var(--pf-primary-text, #ffffff)",

    // Semantic P&L colors
    positive: "var(--pf-positive, #059669)",
    positiveBg: "var(--pf-positive-bg, rgba(5, 150, 105, 0.10))",
    positiveBorder: "var(--pf-positive-border, rgba(5, 150, 105, 0.24))",
    negative: "var(--pf-negative, #dc2626)",
    negativeBg: "var(--pf-negative-bg, rgba(220, 38, 38, 0.10))",
    negativeBorder: "var(--pf-negative-border, rgba(220, 38, 38, 0.24))",
    warning: "var(--pf-warning, #d97706)",
    warningBg: "var(--pf-warning-bg, #fffbeb)",
    warningBorder: "var(--pf-warning-border, rgba(217, 119, 6, 0.30))",

    // Typography
    fontSans:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontMono:
        '"JetBrains Mono", source-code-pro, Menlo, Monaco, Consolas, monospace',

    // Nested radius scale (4px grid)
    radiusTag: "6px",
    radiusControl: "8px",
    radiusCard: "12px",
    radiusPanel: "16px",

    // Layout
    containerMaxWidth: "720px",
};

export const resolveThemeMode = (theme) => {
    if (!theme || typeof theme !== "object") {
        return "light";
    }
    if (theme.mode === "auto" || theme.isAuto === true) {
        return "auto";
    }
    if (theme.mode === "dark" || theme.label === "dark") {
        return "dark";
    }
    return "light";
};

export const getSystemPrefersDark = () => {
    if (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function"
    ) {
        try {
            const media = window.matchMedia("(prefers-color-scheme: dark)");
            return Boolean(media && media.matches);
        } catch (e) {
            return false;
        }
    }
    return false;
};

export const resolveEffectiveTheme = (
    theme,
    systemPrefersDark = getSystemPrefersDark()
) => {
    const mode = resolveThemeMode(theme);
    if (mode === "auto") {
        return systemPrefersDark ? "dark" : "light";
    }
    return mode === "dark" ? "dark" : "light";
};

export const buildThemeSetting = (mode) => {
    if (mode === "auto") {
        return {
            isAuto: true,
            label: getSystemPrefersDark() ? "dark" : "light",
            mode: "auto",
            v: 2,
        };
    }
    const normalized = mode === "dark" ? "dark" : "light";
    return {
        isAuto: false,
        label: normalized,
        mode: normalized,
        v: 2,
    };
};

export const applyThemeToDocument = (effectiveTheme = "light") => {
    if (typeof document === "undefined") return;
    const isDark = effectiveTheme === "dark";
    const palette = isDark ? DarkTokens : LightTokens;
    const cssVarMap = {
        "--pf-deepest": palette.deepest,
        "--pf-bg": palette.bg,
        "--pf-card": palette.card,
        "--pf-raised": palette.raised,
        "--pf-surface": palette.surface,
        "--pf-hairline": palette.hairline,
        "--pf-hairline-strong": palette.hairlineStrong,
        "--pf-top-highlight": palette.topHighlight,
        "--pf-text-primary": palette.textPrimary,
        "--pf-text-secondary": palette.textSecondary,
        "--pf-text-muted": palette.textMuted,
        "--pf-accent": palette.accent,
        "--pf-accent-subtle": palette.accentSubtle,
        "--pf-accent-border": palette.accentBorder,
        "--pf-primary-fill": palette.primaryFill,
        "--pf-primary-text": palette.primaryText,
        "--pf-positive": palette.positive,
        "--pf-positive-bg": palette.positiveBg,
        "--pf-positive-border": palette.positiveBorder,
        "--pf-negative": palette.negative,
        "--pf-negative-bg": palette.negativeBg,
        "--pf-negative-border": palette.negativeBorder,
        "--pf-warning": palette.warning,
        "--pf-warning-bg": palette.warningBg,
        "--pf-warning-border": palette.warningBorder,
    };

    const targets = [document.documentElement, document.body].filter(Boolean);
    targets.forEach((el) => {
        el.classList.toggle("theme-dark", isDark);
        el.classList.toggle("theme-light", !isDark);
        el.setAttribute("data-theme", isDark ? "dark" : "light");
        Object.entries(cssVarMap).forEach(([key, val]) => {
            el.style.setProperty(key, val);
        });
    });

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
        metaTheme.setAttribute("content", palette.bg);
    }
};

export default Tokens;
